import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import {
    getEmployeeTimeCardGrouped,
    getEmployeeTimeCardSummary,
    getThisWeekTimeCardSummary,
    getTodayTimeCardSummary,
} from "../../../redux/features/EmployeeTimeCard";
import { getPayperiod } from "../../../redux/features/payperiod";
import { convertDecimalHoursToHM, formatTimeCardDate, formatTimeCardTime } from "../../../utils/employeeTimeCardhelper";
import { ChevronRight, Loader, ArrowLeft, Users, ArrowRight } from "lucide-react";
import Select from "react-select";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { ManagerTimesheetEmployee } from "../../../types";
import { setGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { isPrivilege, UserPrivileges } from "../../../rules";

interface ManagerEmployeeTimesheetDetailProps {
    employeeId?: string;
}

const ManagerEmployeeTimesheetDetail: React.FC<ManagerEmployeeTimesheetDetailProps> = ({
    employeeId: propEmployeeId,
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { employeeId: paramEmployeeId } = useParams(); // From URL if navigated directly
    const { setSourcePath } = useNavigationHistory();
    // Use prop employeeId if provided, otherwise use from URL params
    // activeEmployeeId will be a string, which is what most APIs expect
    const activeEmployeeId = propEmployeeId || paramEmployeeId;

    const { todaySummary, groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);
    const { employeeRecords } = useSelector((state: RootState) => state.timesheet);
    const [currentEmployee, setCurrentEmployee] = useState<ManagerTimesheetEmployee | null>(null);
    const user = useSelector((state: RootState) => state.user.user);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");
    const [selectedPayperiod, setSelectedPayperiod] = useState<string>(
        localStorage.getItem("managerSelectedPayperiod") || ""
    );

    const isAdmin = isPrivilege(user?.employee_access_role_privileges, UserPrivileges.EMPLOYEE_PRIV_ADMIN);
    const isAllowedToEdit = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET
    );
    const isManagerPrivilege = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.USER_PRIV_UPDATE_REPORTING_EMP_TIMESHEETS
    );
    const isManager = user?.is_manager;

    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;

    const canEdit = isManager ? managerPrivilege : selfPrivilege;

    useEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet Detail"));
        setSourcePath("/m-manager-timesheet");

        setGlobalBackHandler(() => {
            navigate("/m-manager-timesheet");
        });
    }, [dispatch, navigate, setSourcePath]);

    useEffect(() => {
        // Find the current employee from records and fetch their data
        const match = employeeRecords?.find((emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId));
        setCurrentEmployee(match || null);

        // Fetch data for the new employee
        if (activeEmployeeId) {
            setLoading(true);
            // Get pay periods for the new employee (expects employee_id as string)
            dispatch(getPayperiod({ employee_id: activeEmployeeId, count: -5 }));

            // If we have dates, also fetch timesheet data
            if (selectedStartDate && selectedEndDate) {
                const apiCalls = [
                    // groupedTimesheets API expects employee_id as string
                    dispatch(
                        getEmployeeTimeCardGrouped({
                            start_date: selectedStartDate,
                            end_date: selectedEndDate,
                            employee_id: activeEmployeeId,
                            record_type: "A",
                            approval_status: "",
                            sort_by: "os_emp_start_datetime",
                            sort_order: "ASC",
                            group_by: "date",
                            page: "1",
                        })
                    ),
                    // Summary APIs expect employee_id as number
                    dispatch(getTodayTimeCardSummary(Number(activeEmployeeId))),
                    dispatch(getThisWeekTimeCardSummary(Number(activeEmployeeId))),
                ];

                Promise.all(apiCalls)
                    .then(() => setLoading(false))
                    .catch((error) => {
                        console.error("Error fetching employee data:", error);
                        setLoading(false);
                    });
            }
        }
    }, [dispatch, activeEmployeeId, selectedStartDate, selectedEndDate, employeeRecords]);

    const [payperiodOptions, setPayperiodOptions] = useState<any[]>([]);
    const prevStartDateRef = useRef<string>("");
    const prevEndDateRef = useRef<string>("");
    const [loading, setLoading] = useState<boolean>(true);

    // useEffect(() => {
    //     if (currentEmployee?.emp_detail?.emp_name) {
    //         dispatch(setTopNavNameTitle(`Timesheet - ${currentEmployee.emp_detail.emp_name}`));
    //     }
    // }, [currentEmployee, dispatch]);

    useEffect(() => {
        if (payperiodFilters?.length) {
            const newPayperiodOptions = payperiodFilters.map((payperiod) => ({
                value: `${payperiod.start_datetime} - ${payperiod.end_datetime}`,
                label: `${payperiod.start_date_ui} - ${payperiod.end_date_ui}`,
            }));

            const storedPayperiod = localStorage.getItem("managerSelectedPayperiod");

            if (storedPayperiod) {
                const selectedOption = newPayperiodOptions.find((option) => option.value === storedPayperiod);
                if (selectedOption) {
                    setSelectedPayperiod(selectedOption.value);
                    const [startDatetime, endDatetime] = storedPayperiod.split(" - ");
                    handlePayperiodChange(startDatetime, endDatetime);
                }
            } else {
                const firstOption = newPayperiodOptions[0]?.value;
                if (firstOption) {
                    setSelectedPayperiod(firstOption);
                    localStorage.setItem("managerSelectedPayperiod", firstOption);
                    const [startDatetime, endDatetime] = firstOption.split(" - ");
                    handlePayperiodChange(startDatetime, endDatetime);
                }
            }

            setPayperiodOptions(newPayperiodOptions);
        }
    }, [payperiodFilters]);

    const handlePayperiodChange = (startDate: string, endDate: string) => {
        if (startDate === prevStartDateRef.current && endDate === prevEndDateRef.current) {
            return;
        }

        setLoading(true);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;

        if (activeEmployeeId) {
            const apiCalls = [
                dispatch(
                    getEmployeeTimeCardGrouped({
                        start_date: startDate,
                        end_date: endDate,
                        employee_id: activeEmployeeId,
                        record_type: "A",
                        approval_status: "",
                        sort_by: "os_emp_start_datetime",
                        sort_order: "ASC",
                        group_by: "date",
                        page: "1",
                    })
                ),
                dispatch(getTodayTimeCardSummary(Number(activeEmployeeId))),
                dispatch(getThisWeekTimeCardSummary(Number(activeEmployeeId))),
            ];

            Promise.all(apiCalls)
                .then(() => setLoading(false))
                .catch((error) => {
                    console.error("Error with API calls:", error);
                    setLoading(false);
                });
        }
    };

    const handlePayperiodDropdownChange = (selectedOption: any) => {
        setSelectedPayperiod(selectedOption?.value);
        localStorage.setItem("managerSelectedPayperiod", selectedOption?.value);
        const [startDatetime, endDatetime] = selectedOption.value.split(" - ");
        handlePayperiodChange(startDatetime, endDatetime);
    };

    // This is redundant as we've already set up the back handler in useEffect

    if (!activeEmployeeId || !currentEmployee) {
        return (
            <div className="container mx-auto bg-white p-4">
                <div className="text-center text-gray-500">No employee selected or employee not found.</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto bg-white">
            <div className="flex flex-col text-gray-800 text-base font-sans">
                {/* Header with navigation */}
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => {
                                if (!employeeRecords?.length) return;
                                const currentIndex = employeeRecords.findIndex(
                                    (emp) => emp.emp_detail?.employee_id == activeEmployeeId
                                );
                                console.log("Current index:", currentIndex);

                                let targetIndex;
                                if (currentIndex > 0) {
                                    targetIndex = currentIndex - 1;
                                } else {
                                    targetIndex = employeeRecords.length - 1;
                                }

                                const targetEmployee = employeeRecords[targetIndex];
                                if (targetEmployee?.emp_detail?.employee_id) {
                                    console.log("Navigating to:", targetEmployee.emp_detail.employee_id);
                                    navigate(`/m-manager-timesheet/${targetEmployee.emp_detail.employee_id}`);
                                }
                            }}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                            disabled={!employeeRecords || employeeRecords.length <= 1}
                        >
                            <ArrowLeft
                                size={20}
                                className={
                                    !employeeRecords || employeeRecords.length <= 1 ? "text-gray-300" : "text-blue-600"
                                }
                            />
                        </button>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="text-lg font-medium text-gray-900">
                            {currentEmployee.emp_detail?.emp_name || "Unknown"}
                        </div>
                        <div className="text-sm text-gray-500">
                            {currentEmployee.department?.department_name || "No Department"}
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => {
                                if (!employeeRecords?.length) return;
                                const currentIndex = employeeRecords.findIndex(
                                    (emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId)
                                );
                                console.log("Current index:", currentIndex);

                                let targetIndex;
                                if (currentIndex < employeeRecords.length - 1) {
                                    targetIndex = currentIndex + 1;
                                } else {
                                    targetIndex = 0;
                                }

                                const targetEmployee = employeeRecords[targetIndex];
                                if (targetEmployee?.emp_detail?.employee_id) {
                                    console.log("Navigating to:", targetEmployee.emp_detail.employee_id);
                                    navigate(`/m-manager-timesheet/${targetEmployee.emp_detail.employee_id}`);
                                }
                            }}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                            disabled={!employeeRecords || employeeRecords.length <= 1}
                        >
                            <ArrowRight
                                size={20}
                                className={
                                    !employeeRecords || employeeRecords.length <= 1 ? "text-gray-300" : "text-blue-600"
                                }
                            />
                        </button>

                        {/* <button
                            onClick={() => navigate("/m-manager-timesheet")}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                        >
                            <Users size={20} className="text-blue-600" />
                        </button> */}
                    </div>
                </div>

                {/* Pay Period Selector */}
                <div className="px-4 pt-3">
                    <Select
                        value={payperiodOptions?.find((option) => option?.value === selectedPayperiod)}
                        onChange={handlePayperiodDropdownChange}
                        options={payperiodOptions}
                        className="block w-full"
                        classNamePrefix="react-select"
                        isSearchable={false}
                        placeholder="Select a pay period"
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        styles={{
                            control: (baseStyles) => ({
                                ...baseStyles,
                                borderColor: "#e5e7eb",
                                backgroundColor: "#f9fafb",
                                ":hover": {
                                    borderColor: "#e5e7eb",
                                },
                            }),
                            option: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "white",
                                color: state.isSelected ? "white" : "#374151",
                            }),
                        }}
                    />
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center py-6">
                        <Loader
                            size={32}
                            color="#2563eb"
                            strokeWidth={2.5}
                            absoluteStrokeWidth
                            className="animate-spin"
                        />
                    </div>
                )}

                {/* Timesheet Content */}
                {!loading && (
                    <>
                        {/* Summary */}
                        <div className="px-4 py-3">
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="text-gray-600 text-sm">Pay Period Time</div>
                                        <div className="text-2xl font-medium">
                                            {convertDecimalHoursToHM(
                                                groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0
                                            )}
                                        </div>
                                    </div>
                                    {canEdit && (
                                        <button
                                            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium text-sm"
                                            onClick={() => {
                                                navigate("/m-add-timesheet-entry", {
                                                    state: {
                                                        employeeId: activeEmployeeId,
                                                        employeeName: currentEmployee.emp_detail?.emp_name,
                                                        isManager: true,
                                                        mode: "create",
                                                        managerReturnPath: `/m-manager-timesheet/${activeEmployeeId}`,
                                                        managerView: {
                                                            department: currentEmployee.department?.name,
                                                            departmentCode: currentEmployee.department?.code,
                                                        },
                                                    },
                                                });
                                            }}
                                        >
                                            Add entry
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Timesheet Entries */}
                        <div className="px-4 pb-16">
                            {groupedTimesheets?.data?.timesheet_entries_by_date &&
                            Object.keys(groupedTimesheets.data.timesheet_entries_by_date).length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    No timecard data available for this period.
                                </div>
                            ) : (
                                Object.entries(groupedTimesheets?.data?.timesheet_entries_by_date || {}).map(
                                    ([dateKey, entries]) => {
                                        const totalHours = entries.reduce(
                                            (acc, entry) => acc + (Number(entry?.hours) || 0),
                                            0
                                        );
                                        return (
                                            <div key={dateKey} className="border-t flex items-start mb-4 py-1">
                                                <div className="w-2/6 font-medium text-lg text-blue-500">
                                                    {formatTimeCardDate(dateKey)}
                                                </div>
                                                <div className="w-4/6">
                                                    <div className="text-lg text-gray-500 font-medium">
                                                        {convertDecimalHoursToHM(totalHours)}
                                                    </div>
                                                    {entries.map((entry: any, index: number) => (
                                                        <div
                                                            key={index}
                                                            className="text-base cursor-pointer pt-1"
                                                            onClick={() => {
                                                                navigate(`/m-timecard-record-detail/${entry.id}`, {
                                                                    state: {
                                                                        employeeId: activeEmployeeId,
                                                                        recordData: entry,
                                                                        mode: "view",
                                                                    },
                                                                });
                                                            }}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <div className="text-sm text-gray-500">
                                                                    {formatTimeCardTime(entry?.start_datetime || "")} –{" "}
                                                                    {formatTimeCardTime(entry?.end_datetime || "")}
                                                                </div>
                                                                <div className="text-sm text-gray-500 flex items-center">
                                                                    ({convertDecimalHoursToHM(entry?.hours)}){" "}
                                                                    <ChevronRight
                                                                        size={16}
                                                                        color="#000032"
                                                                        strokeWidth={1}
                                                                        absoluteStrokeWidth
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ManagerEmployeeTimesheetDetail;
